import {
  ServiceCreateModal,
  ServiceDetailsModal,
  ServiceListItem,
} from "@okeys/shared-react-components"
import { Service, possibleBeautyCategory } from "@okeys/shared-utils"
import { AlertCircle, Plus } from "lucide-react"
import { useMemo, useState } from "react"
import CategoryTabsHeader from "../../components/Services/CategoryTabsHeader"
import ListServicesLoader from "../../components/Services/ListServicesLoader"
import ServicesFilters from "../../components/Services/ServicesFilters"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import { Tabs, TabsContent } from "../../components/ui/tabs"
import {
  useAddServiceMutation,
  useServicesQuery,
  useUpdateServiceMutation,
} from "../../query/serviceQuery"

type ServicesByCategory = Record<string, Service<"beauty" | "beautyMachine">[]>

// types extraits

export const ListServices = () => {
  const { services, pendingServices, errorServices } = useServicesQuery()
  const updateServiceMutation = useUpdateServiceMutation()
  const addServiceMutation = useAddServiceMutation()
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("all")
  const [selectedService, setSelectedService] = useState<Service<
    "beauty" | "beautyMachine"
  > | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Regroupement des services par catégorie
  const servicesByCategory = useMemo<ServicesByCategory>(() => {
    if (!services) return {}

    return services.reduce<ServicesByCategory>((acc, service) => {
      const category = service.category || "Autres"
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(service)
      return acc
    }, {})
  }, [services])

  // Filtrage par recherche
  const filteredServices = useMemo(() => {
    if (!services) return {} as ServicesByCategory

    const filtered = services.filter(
      (service) =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.category.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Filtrer par catégorie sélectionnée
    const byCategorySource =
      selectedCategory === "all"
        ? filtered
        : filtered.filter((s) => s.category === selectedCategory)

    // Regrouper par catégorie
    return byCategorySource.reduce<ServicesByCategory>((acc, service) => {
      const category = service.category || "Autres"
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(service)
      return acc
    }, {})
  }, [services, searchTerm, selectedCategory])

  const categories = Object.keys(servicesByCategory)
  const totalServices = services?.length || 0

  // États de chargement et d'erreur
  if (pendingServices) {
    return <ListServicesLoader />
  }

  if (errorServices) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground">
            Erreur de chargement
          </h3>
          <p className="text-muted-foreground">
            Impossible de charger les services. Veuillez réessayer.
          </p>
        </div>
      </div>
    )
  }

  if (!services || services.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground">
            Aucun service trouvé
          </h3>
          <p className="text-muted-foreground mb-4">
            Commencez par ajouter des services à votre boutique.
          </p>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un service
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* En-tête + CTA */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-row justify-between w-full col-span-2 md:col-span-1 md:col-start-auto md:row-start-1 md:justify-self-end">
          <ServiceCreateModal
            open={isCreateModalOpen}
            onOpenChange={setIsCreateModalOpen}
            categories={possibleBeautyCategory}
            onSave={async (serviceData) => {
              try {
                await addServiceMutation.mutateAsync({
                  shopId: null,
                  groupShopId: null,
                  shopIdExclusion: [],
                  type: serviceData.type,
                  name: serviceData.name,
                  description: serviceData.description,
                  price: serviceData.price,
                  duration: serviceData.duration,
                  fidelityPoints: serviceData.fidelityPoints,
                  fidelityExclusion: serviceData.fidelityExclusion,
                  fidelityPercentageDiscount:
                    serviceData.fidelityPercentageDiscount,
                  isMultipleService: serviceData.isMultipleService,
                  multipleServiceCount: serviceData.multipleServiceCount,
                  category: serviceData.category.toLowerCase(),
                  sexe: serviceData.sexe,
                  taxInfo: {
                    type: "standard",
                    countryCode: "FR",
                  },
                })
                setIsCreateModalOpen(false)
              } catch (error) {
                console.error("Erreur lors de la création du service:", error)
              }
            }}
            onCancel={() => {
              setIsCreateModalOpen(false)
            }}
            trigger={
              <Button onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nouveau service
              </Button>
            }
          />

          <ServicesFilters
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
          />
        </div>
      </div>

      {/* Onglets par catégorie */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <CategoryTabsHeader
          categories={categories}
          servicesByCategory={servicesByCategory}
          totalServices={totalServices}
          onSelect={(cat) => setSelectedCategory(cat)}
        />

        <TabsContent
          value={selectedCategory}
          className="space-y-4  overflow-y-auto max-h-[calc(100vh-250px)]"
        >
          {Object.values(filteredServices).every(
            (arr: Service<"beauty" | "beautyMachine">[]) => arr.length === 0
          ) ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Aucun résultat avec ces filtres.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm("")
                  }}
                >
                  Réinitialiser
                </Button>
              </div>
            </div>
          ) : (
            Object.entries(filteredServices).map(
              ([category, categoryServices]) => (
                <div key={category} className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-semibold text-foreground">
                      {category}
                    </h2>
                    <Badge variant="secondary">
                      {categoryServices.length} service
                      {categoryServices.length > 1 ? "s" : ""}
                    </Badge>
                  </div>

                  <div className="flex flex-row flex-wrap gap-4">
                    {categoryServices.map(
                      (service: Service<"beauty" | "beautyMachine">) => (
                        <ServiceListItem
                          key={service.id}
                          service={{
                            id: service.id,
                            shopId: service.shopId,
                            name: service.name,
                            price: service.price,
                            duration: service.duration,
                            description: service.description,
                            fidelityPoints: service.fidelityPoints,
                            fidelityExclusion: service.fidelityExclusion,
                            fidelityPercentageDiscount:
                              service.fidelityPercentageDiscount,
                            isMultipleService: service.isMultipleService,
                            multipleServiceCount: service.multipleServiceCount,
                            type: service.type,
                            sexe: service.sexe,
                            category: service.category,
                          }}
                          onClick={() => {
                            setSelectedService(service)
                            setIsEditModalOpen(true)
                          }}
                        />
                      )
                    )}
                  </div>
                </div>
              )
            )
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de modification de service */}
      {selectedService && (
        <ServiceDetailsModal
          service={{
            id: selectedService.id,
            shopId: selectedService.shopId,
            groupShopId: null,
            shopIdExclusion: [],
            name: selectedService.name,
            price: selectedService.price,
            duration: selectedService.duration,
            description: selectedService.description,
            fidelityPoints: selectedService.fidelityPoints,
            fidelityExclusion: selectedService.fidelityExclusion,
            fidelityPercentageDiscount:
              selectedService.fidelityPercentageDiscount,
            isMultipleService: selectedService.isMultipleService,
            multipleServiceCount: selectedService.multipleServiceCount,
            type: selectedService.type,
            sexe: selectedService.sexe,
            category: selectedService.category,
            nonParticipatingServiceProvider: [],
          }}
          open={isEditModalOpen}
          onOpenChange={(open) => {
            setIsEditModalOpen(open)
            if (!open) {
              setSelectedService(null)
            }
          }}
          onSave={async (updatedService) => {
            console.log("updatedService", updatedService)
            try {
              await updateServiceMutation.mutateAsync({
                serviceId: updatedService.id,
                name: updatedService.name,
                description: updatedService.description,
                price: updatedService.price,
                duration: updatedService.duration,
                fidelityPoints: updatedService.fidelityPoints,
                fidelityExclusion: updatedService.fidelityExclusion,
                isMultipleService: updatedService.isMultipleService,
                multipleServiceCount: updatedService.multipleServiceCount,
                category: updatedService.category,
                sexe: updatedService.sexe,
                fidelityPercentageDiscount:
                  updatedService.fidelityPercentageDiscount,
              })
              setIsEditModalOpen(false)
              setSelectedService(null)
            } catch (error) {
              console.error("Erreur lors de la mise à jour du service:", error)
            }
          }}
          onCancel={() => {
            setIsEditModalOpen(false)
            setSelectedService(null)
          }}
        />
      )}
    </div>
  )
}
